import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { QrCodeIcon } from '@icons';

import { Button, Icon, Modal, Spinner, Tooltip } from '@components';
import CurrencyAmount from '@components/currency-amount/CurrencyAmount';
import { AccountType, OrgStatus } from '@constants/generic';
import { STORAGE_KEYS } from '@constants/storage';
import { useLazyGetLeadPaymentOrderDetailsQuery } from '@features/lead-management/queries/LeadManagementQuery';
import { RoleType } from '@features/team-management/constants';
import { TransactionStatus } from '@features/transactions/constants';
import {
  getPaymentDetails,
  getTransactionStatus
} from '@features/transactions/helper';
import { useLazyGetPaymentDetailsByIdQuery } from '@features/transactions/queires/TransactionsQuery';
import { DEFAULT_CURRENCY_CONFIG } from '@hooks/useCurrency';
import { MemberTransactionType } from '@types';
import { getFormattedPhoneNumber } from '@utils/generic';
import { getLocalStorageItem } from '@utils/storage';

import FeeConfigAccordion from '../fee-config-accordion/FeeConfigAccordion';
import AttendanceSessionDetails from './AttendanceSessionDetails';
import PaymentDetailsCard from './PaymentDetailsCard';
import PaymentHistory from './PaymentHistory';
import QuickCollect from './QuickCollect';
import QuickCollectQrModal from './QuickCollectQrModal';

interface PaymentDetailsProps {
  isOpen: boolean;
  handleCancel: () => void;
  branchId: string;
  orderId: string;
  handleDownload: () => void;
  leadId?: string;
}

const PaymentDetails = ({
  isOpen,
  handleCancel,
  branchId,
  orderId,
  handleDownload,
  leadId
}: PaymentDetailsProps) => {
  const { currentOrg, currencyConfig } = useSelector(
    (state: any) => state.user
  );

  const isDefaultCurrency =
    currencyConfig?.code === DEFAULT_CURRENCY_CONFIG.code;

  const [paymentDetails, setPaymentDetails] = useState<MemberTransactionType>();

  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const { rolesAndPermissions } = useSelector((state: any) => state.user);
  const isPaymentManager =
    rolesAndPermissions?.role === RoleType.PAYMENT_MANAGER;

  const [
    getPaymentDetailsById,
    {
      isLoading: paymentDetailsLoading,
      isFetching: paymentDetailsFetching,
      isUninitialized: paymentDetailsUninitialized
    }
  ] = useLazyGetPaymentDetailsByIdQuery();

  const [
    getLeadPaymentOrderDetails,
    {
      isLoading: leadPaymentDetailsLoading,
      isFetching: leadPaymentDetailsFetching,
      isUninitialized: leadPaymentDetailsUninitialized
    }
  ] = useLazyGetLeadPaymentOrderDetailsQuery();

  const handleGetPaymentDetailsById = async () => {
    if (leadId) {
      const res = await getLeadPaymentOrderDetails({
        organizationId,
        leadId,
        branchId,
        paymentOrderId: orderId
      });
      if ('data' in res) {
        setPaymentDetails(res.data?.data || res.data);
      }
    } else {
      const res = await getPaymentDetailsById({
        params: { organizationId, branchId, orderId }
      });
      if ('data' in res) {
        setPaymentDetails(res.data);
      }
    }
  };

  const [isQrCodeOpen, setIsQrCodeOpen] = useState(false);

  const handleOpenModal = () => {
    setIsQrCodeOpen(true);
  };

  const handleCloseModal = () => {
    setIsQrCodeOpen(false);
  };

  const transactionStatus = getTransactionStatus(
    paymentDetails?.status || '',
    paymentDetails?.settlement_status || ''
  );

  const isLoadingPaymentDetails = leadId
    ? leadPaymentDetailsLoading ||
      leadPaymentDetailsFetching ||
      leadPaymentDetailsUninitialized
    : paymentDetailsLoading ||
      paymentDetailsFetching ||
      paymentDetailsUninitialized;

  const totalAmount =
    Number(paymentDetails?.closed_amount) > 0
      ? parseFloat(paymentDetails?.total_amount || '').toFixed(2)
      : parseFloat(
          (
            Number(paymentDetails?.total_amount) +
            Number(paymentDetails?.closed_amount)
          ).toString()
        ).toFixed(2);

  const isOrgLite =
    (currentOrg?.status === OrgStatus.KYC_PENDING ||
      currentOrg?.status === OrgStatus.KYC_SUBMITTED) &&
    currentOrg?.type === AccountType.ORGANIZATION &&
    !currentOrg?.is_kyc_deferred;

  const isIndividualLite =
    (currentOrg?.status === OrgStatus.KYC_PENDING ||
      currentOrg?.status === OrgStatus.KYC_SUBMITTED) &&
    currentOrg?.type === AccountType.INDIVIDUAL &&
    !currentOrg?.is_kyc_deferred;

  const showQrCode =
    paymentDetails?.status === 'processing' &&
    !isOrgLite &&
    !isIndividualLite &&
    !paymentDetails?.is_auto_debit_in_progress;

  const renderPaymentDetails = () => (
    <>
      <div className="mx-auto flex flex-col items-center py-6">
        <Icon name="payment-details" size="none" className="mb-4" />
        <p className="max-w-full break-words text-20 text-grey">
          {transactionStatus === TransactionStatus.SETTLED ||
          transactionStatus === TransactionStatus.TO_SETTLE
            ? `Paid by ${paymentDetails?.name || ''}`
            : `To be paid by ${paymentDetails?.name}`}
        </p>
        {!isPaymentManager && (
          <p className="max-w-full text-grey">
            {paymentDetails?.member_phone_number
              ? getFormattedPhoneNumber(paymentDetails?.member_phone_number)
              : ''}
            {paymentDetails?.member_secondary_phone_number &&
              `, ${getFormattedPhoneNumber(
                paymentDetails.member_secondary_phone_number
              )}`}
          </p>
        )}

        <div className="my-2 flex items-center justify-center text-xl font-normal leading-normal lg:text-xl xl:text-2xl 3xl:text-3xl">
          <CurrencyAmount
            value={Number(totalAmount)}
            className="mr-1 gap-1"
            symbolClass={
              isDefaultCurrency
                ? ''
                : '!text-lg lg:!text-md xl:!text-lg 3xl:!text-xl !font-medium'
            }
            valueClass="font-semibold"
          />
        </div>
        <div className="flex min-w-35 items-center justify-center gap-2 rounded-lg bg-primary10 px-4 py-2.5 text-sm">
          {/* eslint-disable-next-line no-nested-ternary */}
          {paymentDetails?.type === 'individual' ? (
            paymentDetails?.group_id ? (
              <Icon name="group-quick-collect" size="none" />
            ) : (
              <Icon name="lightning" stroke="#FFD700" size="none" />
            )
          ) : (
            <Icon name="users-gold" size="none" />
          )}
          {paymentDetails?.note}
        </div>
      </div>
      {Number(paymentDetails?.no_of_sessions ?? 0) > 0 && (
        <AttendanceSessionDetails
          costPerSession={paymentDetails?.cost_per_session}
          noOfSessions={paymentDetails?.no_of_sessions}
        />
      )}
      {(paymentDetails?.fee_amount_configuration ||
        paymentDetails?.fine_amount !== '0' ||
        Number(paymentDetails?.cgst_amount) > 0 ||
        Number(paymentDetails?.sgst_amount) > 0 ||
        Number(paymentDetails?.igst_amount) > 0) && (
        <FeeConfigAccordion
          feeConfig={paymentDetails?.fee_amount_configuration || []}
          className="mb-6"
          fineAmount={paymentDetails?.fine_amount}
          totalAmount={paymentDetails?.total_amount}
          subTotalAmount={paymentDetails?.original_amount_requested}
          closedAmount={paymentDetails?.closed_amount}
          gstInfo={{
            cgstPercent: paymentDetails?.cgst_percentage,
            sgstPercent: paymentDetails?.sgst_percentage,
            igstPercent: paymentDetails?.igst_percentage,
            cgstAmount: paymentDetails?.cgst_amount,
            sgstAmount: paymentDetails?.sgst_amount,
            igstAmount: paymentDetails?.igst_amount,
            totalGst: paymentDetails?.total_gst
          }}
          discountAmount={paymentDetails?.total_discount_amount}
        />
      )}
      <div>
        <div className="w-full rounded-xl border border-grey40 px-4 py-3">
          {paymentDetails &&
            getPaymentDetails(paymentDetails).map((paymentInfo) => (
              <PaymentDetailsCard
                key={`payment-details-card-${paymentInfo.title}`}
                paymentInfo={paymentInfo}
                status={paymentDetails.status}
                totalPaidAmount={paymentDetails.total_paid_amount}
                totalSettledAmount={paymentDetails.total_settled_amount}
                closedAmount={paymentDetails?.closed_amount}
                totalDiscountAmount={paymentDetails?.total_discount_amount}
              />
            ))}
        </div>
      </div>
    </>
  );

  useEffect(() => {
    handleGetPaymentDetailsById();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onCancel={handleCancel}
      isCloseIconRequired
      closeIconClassName="!p-0 mr-6"
    >
      <div
        className={`relative max-h-[90vh] w-118 rounded-lg bg-white p-6 ${
          isQrCodeOpen ? 'hidden' : 'block'
        } ${
          paymentDetails?.type === 'individual' ||
          !paymentDetails?.is_split_payment
            ? 'h-189.5'
            : 'h-206.25'
        }`}
      >
        <div className="absolute left-0 top-6 h-auto pl-6 text-xl">
          Payment Details
        </div>
        {showQrCode && (
          <Button
            variant="transparent"
            handleClick={handleOpenModal}
            className="absolute right-12 top-4 mr-2 !p-0"
            data-tooltip-id="qr-code-icon"
          >
            <QrCodeIcon />
          </Button>
        )}
        <Tooltip id="qr-code-icon" type="text" tooltipText="Show QR Code" />

        {isLoadingPaymentDetails ? (
          <div className="flex h-full items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <>
            <div
              className={`absolute left-0 top-10 my-6 w-full overflow-y-auto px-6 ${
                paymentDetails?.total_paid_amount !== '0'
                  ? 'bottom-17'
                  : 'bottom-0'
              }`}
            >
              <div>
                {paymentDetails?.is_split_payment ? (
                  <>
                    {renderPaymentDetails()}

                    <PaymentHistory
                      branchId={branchId}
                      orderId={orderId}
                      paymentStatus={paymentDetails?.status}
                      paymentSettlementStatus={
                        paymentDetails?.settlement_status
                      }
                      leadId={leadId}
                    />
                  </>
                ) : (
                  paymentDetails && (
                    <div>
                      <QuickCollect
                        memberDetails={paymentDetails}
                        branchId={branchId}
                        orderId={orderId}
                        paymentStatus={paymentDetails?.status}
                        paymentSettlementStatus={
                          paymentDetails?.settlement_status
                        }
                        leadId={leadId}
                      />
                    </div>
                  )
                )}
              </div>
            </div>
            {paymentDetails?.total_paid_amount !== '0' && (
              <div className="absolute bottom-0 left-0 h-auto w-full p-6">
                <Button
                  label="Download"
                  size="full"
                  variant="primary"
                  className="h-[44px]"
                  handleClick={handleDownload}
                />
              </div>
            )}
          </>
        )}
      </div>
      {isQrCodeOpen && (
        <QuickCollectQrModal
          isOpen={isQrCodeOpen}
          paymentDetails={paymentDetails || ({} as MemberTransactionType)}
          onClose={handleCloseModal}
        />
      )}
    </Modal>
  );
};

export default PaymentDetails;
